import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, LogIn, UserPlus } from "lucide-react";
import { sha256 } from "js-sha256";

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let encryptedPassword = sha256(password).toString();

      await login(email, encryptedPassword);
      toast.success("¡Bienvenido de vuelta!");

      navigate("/");
    } catch (error) {
      const message = error.response?.data?.detail || "Error al procesar solicitud";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Form Section */}
      <div className="login-form-section">
        <div className="max-w-md mx-auto w-full">
          {/* Logo */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-2">
              <img
                src="/logoapplogin.png"
                alt="OHTSU Del Caribe"
                className="h-14 m-5 w-auto"
              />
            </div>
            <p className="text-slate-500">Portal de Ventas para los Vendedores.</p>
          </div>

          {/* Toggle */}
          <div className="flex bg-slate-100 rounded p-1 mb-8">
            <button
              type="button"
              data-testid="login-tab"
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 px-4 rounded text-sm font-medium transition-colors ${isLogin ? "bg-white text-[#0F172A] shadow-sm" : "text-slate-500"
                }`}
            >
              Iniciar Sesión
            </button>

          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">


            <div className="space-y-2">
              <Label htmlFor="email">Correo electrónico</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                placeholder="correo@empresa.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="password-input"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  data-testid="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="submit-btn"
              disabled={loading}
              className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white font-medium"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                  Procesando...
                </span>
              ) : isLogin ? (
                <span className="flex items-center gap-2">
                  <LogIn size={18} />
                  Iniciar Sesión
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <UserPlus size={18} />
                  Crear Cuenta
                </span>
              )}
            </Button>
          </form>


        </div>
      </div>

      {/* Hero Section */}
      <div className="login-hero-section">
        <img
          src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80"
          alt="Office"
          className="login-hero-image"
        />
        <div className="login-hero-overlay">
          <blockquote className="text-white/90 text-xl font-light italic mb-4">
            {/* "Calidad y rendimiento en cada kilómetro recorrido." */}
          </blockquote>
          <p className="text-white/70 text-sm">— Dasoft SRL</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
